import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, AlertTriangle } from 'lucide-react';

// Role hierarchy - which roles can access which dashboards
const ROLE_PERMISSIONS = {
  admin: ['executive', 'compliance', 'analyst', 'auditor'],
  cso: ['executive', 'compliance', 'analyst', 'auditor'],
  ceo: ['executive'],
  compliance_manager: ['compliance', 'analyst'],
  analyst: ['analyst'],
  auditor: ['auditor', 'compliance', 'analyst'] // Read-only for compliance/analyst
};

// Dashboard metadata
const DASHBOARD_INFO = {
  executive: {
    name: 'Executive Hub',
    description: 'Strategic ESG overview for C-suite executives',
    requiredRoles: ['admin', 'cso', 'ceo'],
    icon: '📊'
  },
  compliance: {
    name: 'Compliance Console',
    description: 'Regulatory tracking and gap analysis',
    requiredRoles: ['admin', 'cso', 'compliance_manager', 'auditor'],
    icon: '🔒'
  },
  analyst: {
    name: 'Data Lab',
    description: 'Granular data analysis and methodology',
    requiredRoles: ['admin', 'cso', 'compliance_manager', 'analyst', 'auditor'],
    icon: '📈'
  },
  auditor: {
    name: 'Audit Vault',
    description: 'Audit trails and integrity verification',
    requiredRoles: ['admin', 'cso', 'auditor'],
    icon: '🔍'
  }
};

/**
 * Check if a user role has access to a specific dashboard
 * @param {string} userRole - The user's role
 * @param {string} dashboardId - The dashboard identifier
 * @returns {boolean} - Whether access is permitted
 */
export const hasAccessToDashboard = (userRole, dashboardId) => {
  const permissions = ROLE_PERMISSIONS[userRole] || [];
  return permissions.includes(dashboardId);
};

/**
 * Get list of dashboards a role can access
 * @param {string} userRole - The user's role
 * @returns {string[]} - Array of accessible dashboard IDs
 */
export const getAccessibleDashboards = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || [];
};

/**
 * Access Denied Component - Displayed when user lacks permission
 * Compliant with EU accessibility guidelines
 */
const AccessDenied = ({ dashboardId, userRole }) => {
  const navigate = useNavigate();
  const dashboardInfo = DASHBOARD_INFO[dashboardId] || { name: 'Dashboard', description: '' };
  
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="access-denied">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-8 text-center shadow-sm">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
          <Lock size={32} className="text-red-600" />
        </div>
        
        {/* Title */}
        <h1 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Access Denied
        </h1>
        
        {/* Message */}
        <p className="text-slate-600 mb-6">
          You do not have permission to access the <strong>{dashboardInfo.name}</strong>.
        </p>
        
        {/* Details Box */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-slate-900 mb-1">Authorization Required</p>
              <p className="text-slate-600">
                Your current role (<span className="font-mono bg-slate-200 px-1 rounded">{userRole || 'guest'}</span>) 
                does not have access to this dashboard.
              </p>
              <p className="text-slate-500 mt-2 text-xs">
                Required roles: {dashboardInfo.requiredRoles?.join(', ') || 'N/A'}
              </p>
            </div>
          </div>
        </div>
        
        {/* EU Compliance Notice */}
        <div className="text-xs text-slate-500 mb-6 p-3 bg-blue-50 rounded-lg">
          <p className="flex items-center gap-1 justify-center">
            <Shield size={12} />
            <span>Access control compliant with <strong>GDPR Art. 25</strong> (Data Protection by Design)</span>
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
          <button
            onClick={() => navigate('/dashboard-selector')}
            className="flex-1 px-4 py-2.5 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors"
          >
            Dashboard Selector
          </button>
        </div>
        
        {/* Contact Admin */}
        <p className="text-xs text-slate-500 mt-4">
          Need access? Contact your system administrator.
        </p>
      </div>
    </div>
  );
};

/**
 * Protected Dashboard Wrapper Component
 * Wraps persona dashboards with role-based access control
 */
const ProtectedDashboard = ({ dashboardId, userRole, children }) => {
  const hasAccess = hasAccessToDashboard(userRole, dashboardId);
  
  if (!hasAccess) {
    return <AccessDenied dashboardId={dashboardId} userRole={userRole} />;
  }
  
  return children;
};

export default ProtectedDashboard;
export { ROLE_PERMISSIONS, DASHBOARD_INFO, AccessDenied };
